import React from 'react';

import {
  CsvImport,
  keyColumns,
  type CsvSchema,
  type ServerImportSchema
} from '~/components/csv-import';
import {
  applyChangesToDirectory,
  entryToRecord,
  transferDirectorySchema
} from './voice-transfer-directory-csv';
import {
  type VoiceAgentSettingsTransferDirectoryEntryInput,
  type VoiceAgentTransferLineOfBusinessInput
} from './voice_agent_form_input';
import { PRO_ENTRY_LIMIT } from './voice_transfer_directory_table';

export const TransferDirectoryImport = ({
  importSchema,
  directory,
  lineOfBusinessCatalog,
  externalTransferEnabled,
  hasAdvancedCustomization,
  onDirectoryChange
}: {
  /** The server's transfer directory columns. */
  importSchema: ServerImportSchema;
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[];
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[];
  externalTransferEnabled: boolean;
  hasAdvancedCustomization: boolean | undefined;
  onDirectoryChange: (directory: VoiceAgentSettingsTransferDirectoryEntryInput[]) => void;
}) => {
  // The page builds its catalog afresh on every render, and a new schema checks an open
  // file again, so the schema follows the catalog's content.
  const schema = React.useMemo(
    () => transferDirectorySchema(importSchema, lineOfBusinessCatalog, externalTransferEnabled),
    [importSchema, JSON.stringify(lineOfBusinessCatalog), externalTransferEnabled]
  );
  // Imported entries go after the existing ones, and without Pro only the first
  // PRO_ENTRY_LIMIT entries of the whole directory work on calls.
  const entriesLeftOnPlan = hasAdvancedCustomization
    ? undefined
    : Math.max(0, PRO_ENTRY_LIMIT - directory.length);
  return (
    <div className="min-w-0">
      <CsvImport
        listName="Transfer directory"
        offerRemoval
        schema={schema}
        maxAdded={entriesLeftOnPlan}
        limitNote={`upgrade to Pro to add more than ${PRO_ENTRY_LIMIT} transfer directory entries`}
        currentData={directory.map((entry) => entryToRecord(entry, lineOfBusinessCatalog))}
        onCommit={async (changes) => {
          onDirectoryChange(
            applyChangesToDirectory(schema, directory, changes, lineOfBusinessCatalog)
          );
          return { status: 'applied' };
        }}
      />
      <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
        Imported entries are added to the directory below. Save this page to keep them.{' '}
        {keyFieldsSentence(schema)} identify an entry. Changing any of them adds a new entry rather
        than updating the existing one.
      </p>
    </div>
  );
};

/** "Name, Type and Aliases", from the schema's key columns. */
const keyFieldsSentence = (schema: CsvSchema): string => {
  const labels = keyColumns(schema).map(
    (key) => schema.fields.find((field) => field.key === key)?.label ?? key
  );
  return labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};
